/** React bindings to the local database: live queries re-render when IndexedDB changes. */
import { useLiveQuery } from "dexie-react-hooks";
import { useMemo } from "react";
import { accountingMonthFromDate } from "@/domain/accounting-period";
import { getDatabase } from "@/data/db";
import { loadFinanceData } from "@/data/finance-data";
import { listExpenses, listIncomes } from "@/data/ledger";
import { getCategoryTree, listPaymentMethods } from "@/data/reference";
import { buildFinanceIndex, type FinanceIndex } from "@/finance/data";
import { todayIso } from "@/lib/clock";

export const db = getDatabase();
export const APP_TIME_ZONE = "Europe/Rome";

export function useToday(): { today: string; month: string; year: number } {
  const today = todayIso(APP_TIME_ZONE);
  return { today, month: accountingMonthFromDate(today), year: Number(today.slice(0, 4)) };
}

export function useFinanceIndex(): FinanceIndex | undefined {
  const data = useLiveQuery(() => loadFinanceData(db), []);
  return useMemo(() => (data ? buildFinanceIndex(data) : undefined), [data]);
}

export function usePaymentMethods(activeOnly = true) {
  return useLiveQuery(() => listPaymentMethods(db, { activeOnly }), [activeOnly]);
}

export function useCategoryTree(includeInactive = false) {
  return useLiveQuery(() => getCategoryTree(db, { includeInactive }), [includeInactive]);
}

export function useExpenses(month: string) {
  return useLiveQuery(() => listExpenses(db, month), [month]);
}

export function useIncomes(month: string) {
  return useLiveQuery(() => listIncomes(db, month), [month]);
}

export function useSetting<T>(key: string, fallback: T): T {
  const row = useLiveQuery(() => db.settings.get(key), [key]);
  return row ? (row.value as T) : fallback;
}

/** Validates `YYYY-MM` from the URL; falls back to the current month. */
export function monthParam(value: string | undefined, fallback: string): string {
  return value && /^\d{4}-(0[1-9]|1[0-2])$/.test(value) ? value : fallback;
}
